import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  cachedItemByIdSelector, // infiniteQueryOptions,
  infiniteQueryOptionsFn,
} from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  AnnualOrderedCapacity,
  AnnualOrderedCapacityCalculateByContractFiler,
  AnnualOrderedCapacityCounterpartiesParams,
  AnnualOrderedCapacityCounterparty,
  AnnualOrderedCapacityPayload,
  AnnualOrderedCapacityQuery,
  AnnualOrderedCapacityQueryGrid,
  AnnualOrderedCapacityUpdateByApplicationPayload,
  CalculateAnnualOrderedCapacityByOrganizationFilter,
  CalculateAnnualOrderedCapacityFiler,
  CapacityType,
  GetAnnualOrderedCapacitiesQueryPayload,
} from './types';

export const annualOrderedCapacitiesApi = createApi({
  reducerPath: 'annualOrderedCapacitiesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/annual-ordered-capacities`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: [
    'AnnualOrderedCapacityQuery',
    'CapacityType',
    'AnnualOrderedCapacity',
    'AnnualOrderedCapacityCounterparties',
  ],
  endpoints: builder => ({
    getAnnualOrderedCapacities: builder.query<
      { items: AnnualOrderedCapacityQuery[]; inProgress: boolean },
      GetAnnualOrderedCapacitiesQueryPayload
    >({
      query: data => ({
        url: '/query',
        method: 'POST',
        data,
      }),
      providesTags: () => ['AnnualOrderedCapacityQuery'],
    }),
    getAnnualOrderedCapacitiesI: builder.infiniteQuery<
      AnnualOrderedCapacityQueryGrid,
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 1000 }),
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => [
        'AnnualOrderedCapacity',
        'AnnualOrderedCapacityCounterparties',
      ],
    }),
    getOrderedCapacityTypes: builder.query<CapacityType[], void>({
      query: () => ({
        url: '/types',
      }),
    }),
    getAnnualOrderedCapacityById: builder.query<AnnualOrderedCapacity, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['AnnualOrderedCapacity'],
    }),
    deleteAnnualOrderedCapacityById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AnnualOrderedCapacity', 'AnnualOrderedCapacityQuery'],
    }),
    annualOrderedCapacityRecalculateSingle: builder.mutation<
      string,
      CalculateAnnualOrderedCapacityFiler
    >({
      query: data => ({
        url: '/recalculate-single',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AnnualOrderedCapacity', 'AnnualOrderedCapacityQuery'],
    }),
    annualOrderedCapacityCalculateByContract: builder.mutation<
      string,
      AnnualOrderedCapacityCalculateByContractFiler
    >({
      query: data => ({
        url: '/calculate-by-contract',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AnnualOrderedCapacityQuery'],
    }),
    calculateByApplication: builder.mutation<any, AnnualOrderedCapacityPayload>(
      {
        query: data => ({
          url: `/calculate-by-application`,
          method: 'POST',
          data,
        }),
        invalidatesTags: [
          'AnnualOrderedCapacity',
          'AnnualOrderedCapacityQuery',
        ],
      },
    ),
    updateByApplication: builder.mutation<
      string,
      AnnualOrderedCapacityUpdateByApplicationPayload
    >({
      query: data => ({
        url: '/update-by-application',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AnnualOrderedCapacity', 'AnnualOrderedCapacityQuery'],
    }),
    calculateAnnualOrderedCapacityByOrganization: builder.mutation<
      void,
      CalculateAnnualOrderedCapacityByOrganizationFilter
    >({
      query: data => ({
        url: '/calculate-by-organization',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AnnualOrderedCapacity', 'AnnualOrderedCapacityQuery'],
    }),
    getAnnualOrderedCapacityCounterparties: builder.query<
      AnnualOrderedCapacityCounterparty[],
      AnnualOrderedCapacityCounterpartiesParams | void
    >({
      query: params => ({
        url: '/counterparty-filters',
        method: 'GET',
        params,
      }),
      providesTags: () => ['AnnualOrderedCapacityCounterparties'],
    }),
    isExistsCalculationByOrganization: builder.query<boolean, { year: number }>(
      {
        query: data => ({
          url: '/is-exists-calculation-by-organization',
          method: 'POST',
          data,
        }),
      },
    ),
    isExistsCalculationByInitialOrder: builder.query<
      boolean,
      {
        year: number;
        periodStart: number;
        periodEnd: number;
        contractId: string;
      }
    >({
      query: data => ({
        url: '/is-exists-calculation-by-initial-order',
        method: 'POST',
        data,
      }),
    }),
    isExistsCalculationByCommercialMeteringPoints: builder.query<
      { commercialMeteringPoints: string[] },
      {
        year: number;
        contractId: string;
        commercialMeteringPoints: string[];
      }
    >({
      query: data => ({
        url: '/is-exists-calculation-by-commercial-metering-points',
        method: 'POST',
        data,
      }),
    }),
    getCommercialMeteringPointFilters: builder.query<
      { id: string; name: string }[],
      { periodStart: number; year: number; contractId: string }
    >({
      query: params => ({
        url: '/commercial-metering-point-filters',
        params,
      }),
    }),
    checkAdjustmentDate: builder.mutation<
      { isValid: boolean; message: string },
      {
        orderedCapacityTypeId: string;
        year: number;
        date: string;
      }
    >({
      query: data => ({
        url: '/is-valid-date',
        method: 'POST',
        data,
      }),
    }),
    createAdjustment: builder.mutation<
      string,
      {
        id: string;
        orderedCapacityTypeId: string;
        date: string;
        items: {
          commercialMeteringPointId: string;
          annualVolume: number;
        }[];
      }
    >({
      query: data => ({
        url: '/adjustment-by-application',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AnnualOrderedCapacity', 'AnnualOrderedCapacityQuery'],
    }),
    updateAdjustment: builder.mutation<
      string,
      {
        id: string;
        items: {
          commercialMeteringPointId: string;
          annualVolume: number;
        }[];
      }
    >({
      query: data => ({
        url: '/adjustment-by-application',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AnnualOrderedCapacity', 'AnnualOrderedCapacityQuery'],
    }),
  }),
});

export const {
  useGetAnnualOrderedCapacitiesQuery,
  useGetAnnualOrderedCapacityByIdQuery,
  useGetOrderedCapacityTypesQuery,
  useDeleteAnnualOrderedCapacityByIdMutation,
  useAnnualOrderedCapacityRecalculateSingleMutation,
  useAnnualOrderedCapacityCalculateByContractMutation,
  useCalculateByApplicationMutation,
  useCalculateAnnualOrderedCapacityByOrganizationMutation,
  useGetAnnualOrderedCapacityCounterpartiesQuery,
  useLazyIsExistsCalculationByOrganizationQuery,
  useGetCommercialMeteringPointFiltersQuery,
  useLazyIsExistsCalculationByInitialOrderQuery,
  useLazyIsExistsCalculationByCommercialMeteringPointsQuery,
  useUpdateByApplicationMutation,
  useCheckAdjustmentDateMutation,
  useCreateAdjustmentMutation,
  useUpdateAdjustmentMutation,
} = annualOrderedCapacitiesApi;

const normalizeInfiniteQueryData =
  (hook: any) => (params?: any, options?: any) => {
    const payload = hook(params, options);

    return {
      ...payload,
      data: (payload.data?.pages || []).flat().reduce((acc, page) => {
        return {
          ...acc,
          ...page,
          items: (acc?.items || []).concat(page.items),
        };
      }, {}),
    };
  };

export const useGetAnnualOrderedCapacitiesIInfiniteQuery =
  normalizeInfiniteQueryData(
    annualOrderedCapacitiesApi.useGetAnnualOrderedCapacitiesIInfiniteQuery,
  );

const getCacheAnnualOrderedCapacityByIdSelector = cachedItemByIdSelector(
  'annualOrderedCapacitiesApi',
  'getAnnualOrderedCapacitiesI',
);

export const useGetCachedAnnualOrderedCapacityByIdSelector = id =>
  useSelector(getCacheAnnualOrderedCapacityByIdSelector(id));
